import React, { useMemo, useEffect } from 'react';
import { SearchableDropdown } from '../../../components/SearchableDropdown';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import FiltersCard from '../../../components/FiltersCard';
import { Form, FilterGrid, FilterField } from '@/components/ui';
import { RoutineTaskFilterOptions, RoutineTaskFilters } from '../../../services/routineTaskService';
import { formatStatusForDisplay } from '../../../utils/statusUtils';

// Define TaskStatus type
type TaskStatus = 
  | 'PROPOSED'
  | 'PLANNED'
  | 'ACTIVE'
  | 'ACTIVE_MARKED_AS_COMPLETE'
  | 'INTERNAL_REVIEW'
  | 'CLOSED'
  | 'FINALIZED'
  | 'CANCELLED';

// Add status options constant with All option
const STATUSES: string[] = [
  'All',
  'ACTIVE',
  'CLOSED',
  'FINALIZED',
  'CANCELLED'
];

// Define priority levels
const PRIORITIES = [
  'Low',
  'Medium',
  'High',
  'Critical'
];

interface RoutineTaskFiltersProps {
  filterOptions: RoutineTaskFilterOptions;
  filters: RoutineTaskFilters;
  onFilterChange: (name: string, value: string | string[]) => void;
  onClearFilters?: () => void;
  showApplyButton?: boolean;
  onApply?: () => void;
  applyDisabled?: boolean;
  hasActiveFilters?: boolean;
}

const RoutineTaskFiltersComponent: React.FC<RoutineTaskFiltersProps> = ({
  filterOptions,
  filters,
  onFilterChange,
  onClearFilters,
  showApplyButton = false,
  onApply,
  applyDisabled = false,
  hasActiveFilters = false,
}) => {
  // Use server-provided options when available, otherwise fall back to defaults
  const statusOptions = filterOptions.statuses.length > 0 ? filterOptions.statuses : STATUSES.slice(1);
  const priorityOptions = filterOptions.priorities.length > 0 ? filterOptions.priorities : PRIORITIES;

  const selectedForemanAreas = useMemo(() => {
    if (Array.isArray(filters.foremanArea)) {
      return filters.foremanArea.map(value => value.toString());
    }
    if (filters.foremanArea) {
      return [filters.foremanArea.toString()];
    }
    return [];
  }, [filters.foremanArea]);

  const filteredForemanAreas = useMemo(() => {
    if (!filters.serviceArea) {
      return filterOptions.foremanAreas;
    }

    return filterOptions.foremanAreas.filter(area => {
      const serviceAreaName = area.serviceAreaName ?? '';
      const serviceAreaId = area.serviceAreaId?.toString() ?? '';
      return serviceAreaName === filters.serviceArea || serviceAreaId === filters.serviceArea;
    });
  }, [filterOptions.foremanAreas, filters.serviceArea]);

  useEffect(() => {
    if (selectedForemanAreas.length === 0) {
      return;
    }

    const validForemanAreas = new Set(filteredForemanAreas.map(area => area.name));
    const updatedSelection = selectedForemanAreas.filter(name => validForemanAreas.has(name));

    if (updatedSelection.length !== selectedForemanAreas.length) {
      onFilterChange('foremanArea', updatedSelection);
    }
  }, [filteredForemanAreas, onFilterChange, selectedForemanAreas]);

  // Handle clear all filters
  const handleClearFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    }
  };

  return (
    <>
      {/* Filters Card */}
      <FiltersCard 
        title="Routine Task Filters"
        subtitle="Filter routine tasks by service area, foreman area, activity, date range, and status"
        collapsible={true}
        defaultCollapsed={false}
        showClearButton={true}
        onClear={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
        showApplyButton={showApplyButton}
        onApply={onApply}
        applyDisabled={applyDisabled}
      >
        {/* Row/Col md={2} pinned every filter to a fixed 2/12 fraction, so six filters truncated
            on a tablet instead of reflowing - FilterGrid auto-fits tracks at a minimum readable
            width instead (see the "Active filters truncating" QA report). */}
        <FilterGrid>
          <FilterField>
            <StandoutDropdown
              type="searchable"
              options={filterOptions.serviceAreas}
              value={filters.serviceArea || ''}
              onChange={(value) => onFilterChange('serviceArea', value)}
              getValue={(area) => area.name}
              formatOption={(area) => area.name}
              formatDisplay={(area) => area.name}
              placeholder="All Service Areas"
              className="tw-w-full"
              label="Service Area"
              noOptionsMessage="No service areas available"
            />
          </FilterField>
          <FilterField>
            <StandoutDropdown
              type="multiselect"
              options={filteredForemanAreas}
              value={selectedForemanAreas}
              onChange={(value) => onFilterChange('foremanArea', value.map(v => String(v)))}
              getValue={(area) => area.name}
              formatOption={(area) => area.name}
              formatDisplay={(area) => area.name}
              placeholder="All Foreman Areas"
              className="tw-w-full"
              label="Foreman Area"
              noOptionsMessage={filters.serviceArea ? "No foreman areas for selected service area" : "No foreman areas available"}
            />
          </FilterField>
          <FilterField>
            <MultiSelectDropdown
              options={filterOptions.activities}
              value={Array.isArray(filters.activityName) ? filters.activityName : (filters.activityName ? [filters.activityName] : [])}
              onChange={(value) => onFilterChange('activityName', value.map(v => String(v)))}
              getValue={(activity) => activity.activityName}
              formatOption={(activity) => `#${activity.activityNumber} - ${activity.activityName}`}
              placeholder="All Activities"
              className="tw-w-full"
              label="Activity"
              noOptionsMessage="No activities available"
            />
          </FilterField>
          <FilterField>
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              name="startDate"
              value={filters.startDate || ''}
              onChange={(e) => onFilterChange(e.target.name, e.target.value)}
            />
          </FilterField>
          <FilterField>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              name="endDate"
              value={filters.endDate || ''}
              onChange={(e) => onFilterChange(e.target.name, e.target.value)}
            />
          </FilterField>
          <FilterField>
            <MultiSelectDropdown<string>
              options={statusOptions}
              value={Array.isArray(filters.status) ? filters.status : (filters.status ? [filters.status] : [])}
              onChange={(value) => onFilterChange('status', value.map(v => String(v)))}
              getValue={(status) => status}
              formatOption={(status) => formatStatusForDisplay(status)}
              placeholder="All Tasks"
              className="tw-w-full"
              label="Status"
              noOptionsMessage="No statuses available"
            />
          </FilterField>
        </FilterGrid>
      </FiltersCard>
    </>
  );
};

export default RoutineTaskFiltersComponent; 